/**
 * A model plate, decoded in the page rather than by the browser's image
 * decoder. Phase 8 plan §9.7.
 *
 * **An image decoder is free to hand back something other than the bytes in
 * the file, and WebKit does.** Measured on one real 2816×1557 `refc` plate,
 * decoded the way this layer used to (`createImageBitmap` with
 * `colorSpaceConversion: 'none'` and `premultiplyAlpha: 'none'`, an `R8`
 * upload, a framebuffer readback): Chromium returned every byte, and WebKit
 * changed **11.75%** of them by ±1 whatever the options said — a dither from
 * colour-managing a gray image. That turned 10.75% of the reserved index (255,
 * "nothing here") into 254, the top of the ramp, which is near-white speckle
 * over every empty lake; and the validity-weighted blend pulled rain edges
 * toward it, which is the rainbow fringe. The Phase 7 gate passed because it
 * decoded a tiny synthetic image, which WebKit gets right, and only ever ran
 * in Chromium.
 *
 * So the plate is decoded here: walk the chunks, inflate the `IDAT`s with the
 * browser's own `DecompressionStream('deflate')` (an `IDAT` stream is zlib,
 * which is what `'deflate'` means there), undo the row filters, and hand back
 * the bytes for a `texImage2D` from a `Uint8Array` — which no engine
 * colour-manages. No dependency: `DecompressionStream` is in every engine
 * MapLibre 6 runs on.
 *
 * **It decodes exactly what `encode_gray` writes** — 8-bit grayscale, not
 * interlaced — and refuses anything else loudly. A plate that is not what the
 * renderer wrote is a bug to see, not one to draw a guess at.
 *
 * **Self-contained on purpose**: no reference to anything outside this
 * function's own body. `web/e2e/data-texture.spec.ts` runs this exact function
 * in WebKit and Chromium by its source text, which is only the same code if it
 * closes over nothing.
 */

export interface GrayImage {
  width: number
  height: number
  /** `width * height` bytes, row 0 first, one byte per texel. */
  data: Uint8Array
}

export async function decodeGrayPng(input: ArrayBuffer | Uint8Array): Promise<GrayImage> {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input)
  const refuse = (why: string): never => {
    throw new Error(`not a plate this app renders: ${why}`)
  }

  const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
  if (bytes.length < 8 || signature.some((b, i) => bytes[i] !== b)) refuse('no PNG signature')

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let width = 0
  let height = 0
  const idat: Uint8Array[] = []
  let ended = false
  for (let pos = 8; pos + 12 <= bytes.length; ) {
    const length = view.getUint32(pos)
    const type = String.fromCharCode(bytes[pos + 4], bytes[pos + 5], bytes[pos + 6], bytes[pos + 7])
    const start = pos + 8
    const end = start + length
    if (end + 4 > bytes.length) refuse(`${type} runs past the end of the file`)
    if (type === 'IHDR') {
      width = view.getUint32(start)
      height = view.getUint32(start + 4)
      const [depth, colour, compression, filter, interlace] = bytes.subarray(start + 8, start + 13)
      if (depth !== 8 || colour !== 0) {
        refuse(`bit depth ${depth}, colour type ${colour}; the renderer writes 8-bit grayscale`)
      }
      if (compression !== 0 || filter !== 0) refuse(`compression ${compression}, filter ${filter}`)
      if (interlace !== 0) refuse('interlaced')
    } else if (type === 'IDAT') {
      idat.push(bytes.subarray(start, end))
    } else if (type === 'IEND') {
      ended = true
      break
    } else if (type.charCodeAt(0) < 0x61) {
      // An uppercase first letter is a *critical* chunk: a decoder that does
      // not understand one must not claim to have decoded the image.
      refuse(`critical chunk ${type}`)
    }
    pos = end + 4
  }
  if (width === 0 || height === 0) refuse('no IHDR')
  if (!ended) refuse('no IEND; truncated')
  if (idat.length === 0) refuse('no IDAT')

  // One filter byte in front of every row.
  const stride = width + 1
  const raw = new Uint8Array(stride * height)
  const inflated = new ReadableStream<Uint8Array>({
    start(controller) {
      for (const part of idat) controller.enqueue(part)
      controller.close()
    },
  }).pipeThrough(new DecompressionStream('deflate') as unknown as TransformStream<Uint8Array, Uint8Array>)
  const reader = inflated.getReader()
  let filled = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (filled + value.length > raw.length) refuse('more image data than IHDR describes')
    raw.set(value, filled)
    filled += value.length
  }
  if (filled !== raw.length) refuse(`${filled} bytes of image data where IHDR needs ${raw.length}`)

  // One byte per pixel, so "the pixel to the left" is the previous byte.
  const out = new Uint8Array(width * height)
  for (let y = 0; y < height; y++) {
    const src = y * stride + 1
    const row = y * width
    const up = row - width
    const filter = raw[src - 1]
    if (filter === 0 || (filter === 2 && y === 0)) {
      out.set(raw.subarray(src, src + width), row)
    } else if (filter === 1) {
      let left = 0
      for (let x = 0; x < width; x++) left = out[row + x] = (raw[src + x] + left) & 0xff
    } else if (filter === 2) {
      for (let x = 0; x < width; x++) out[row + x] = (raw[src + x] + out[up + x]) & 0xff
    } else if (filter === 3) {
      for (let x = 0; x < width; x++) {
        const left = x > 0 ? out[row + x - 1] : 0
        const above = y > 0 ? out[up + x] : 0
        out[row + x] = (raw[src + x] + ((left + above) >> 1)) & 0xff
      }
    } else if (filter === 4) {
      for (let x = 0; x < width; x++) {
        const a = x > 0 ? out[row + x - 1] : 0
        const b = y > 0 ? out[up + x] : 0
        const c = x > 0 && y > 0 ? out[up + x - 1] : 0
        const p = a + b - c
        const pa = Math.abs(p - a)
        const pb = Math.abs(p - b)
        const pc = Math.abs(p - c)
        const predictor = pa <= pb && pa <= pc ? a : pb <= pc ? b : c
        out[row + x] = (raw[src + x] + predictor) & 0xff
      }
    } else {
      refuse(`row ${y} has filter type ${filter}`)
    }
  }
  return { width, height, data: out }
}
